import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'vanilla-lazyload'
import inView from 'in-view'
import { camelCase } from 'changecase-objects'
import { Header, ThemeOverride } from '../core'
import { helpers, prismicHelpers } from '../../helpers'
import TopTenProductCard from './TopTenProductCard'
import DetailedProductCard from './DetailedProductCard'
import HorizontalProductCard from './HorizontalProductCard'
import HorizontalScroller from './HorizontalScroller'

/**
 * Searches for the index of a sku from Skus within orderedSkusList
 *
 * @param {Array<string>} orderedSkusList - The list of skus that are in the desired order
 * @param {Array<string>} skus - The list of skus to search
 *
 * @returns {number} - the index of the matching sku or -1 if not found
 */
const findMatchingSkuIndex = (orderedSkusList = [], skus = []) => {
  let skuIndex = -1
  let currentIndex = 0

  // Searches for a sku within orderedSkusList and ends if one is found, or if we've reached the end of skus
  while (skuIndex === -1 && currentIndex < skus.length) {
    const newIndex = orderedSkusList.indexOf(skus[currentIndex])

    if (newIndex > -1) {
      skuIndex = newIndex
    }

    currentIndex += 1
  }

  return skuIndex
}

/**
 * Creates a list of Skus belonging to a product
 *
 * @param {object} product - A product object
 *
 * @returns {Array<string>} - List of Skus
 */
const getProductSkus = product =>
  product && product.variants.length && product.variants.map(variant => variant.sku || '')

/**
 * Sorts a list of products by a list of ordered skus
 *
 * @param {Array<string>} orderedSkusList - The list of skus that are in the desired order
 * @param {Array<object>} products - This list of products to put in order
 *
 * @returns {Array<object>} - The proper list of products
 */
const sortProductsByList = (orderedSkusList = [], products = []) => {
  return (
    (products &&
      products.length &&
      products.sort((productA, productB) => {
        // Obtain productA skus
        const productASkus = getProductSkus(productA)
        // Locate productA skus index
        const productASkuIndex = findMatchingSkuIndex(orderedSkusList, productASkus)

        // Obtain productB skus
        const productBSkus = getProductSkus(productB)
        // Locate productB skus index
        const productBSkuIndex = findMatchingSkuIndex(orderedSkusList, productBSkus)

        return productASkuIndex - productBSkuIndex
      })) ||
    []
  )
}

/**
 * ProductList component
 *
 * Displays a list of products. If no products are found then nothing is rendered.
 *
 * The way products are displayed depends on the `style` that is passed in:
 * - `Top 10` is for displaying products in a Top 10 list.
 * - `Detailed Cards` is for displaying detailed product cards.
 *
 * @returns {function} React component
 */
const ProductList = ({ data = {}, context = '', isDynamic = false }) => {
  const uid = helpers.generateID('productList')
  const selector = `#${uid}`
  const containerSelector =
    data.style && data.style === 'vertical'
      ? `#${uid} .product-list-content`
      : `#${uid} .scroller-content`
  const [products, setProducts] = useState([])
  const [title, setTitle] = useState(data.title || '')
  const [linkText, setLinkText] = useState(data.linkText || '')
  const [link, setLink] = useState(data.link || '')
  const [listName, setListName] = useState(data.listName || '')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const isHorizontal = data.style !== 'vertical'

  const backgroundClass = isHorizontal ? 'has-dark-background' : ''

  /**
   * `on_set` callback for image lazy load on product cards. Tracks a product
   * impression based on data from the given image.
   *
   * @param {element} image - image element with tracking data attached.
   */
  const trackProductImpression = image => {
    if (!image || !image.dataset.position) {
      return
    }
    const product = products[Number(image.dataset.position) - 1]

    if (!product) {
      return
    }

    let price = null

    let sku = null

    // Parse the price of the variant.
    if (product.variant && product.variant.price) {
      price = product.variant.price
    }

    // Parse the sku of the variant.
    if (product.variant && product.variant.sku) {
      sku = product.variant.sku
    } else {
      sku = product.sku || null
    }

    $.track('product impression', {
      id: sku,
      name: product.name,
      list: listName,
      brand: helpers.formatProducers(product.producers),
      category: product.variant ? product.variant.oracle_department_name : null,
      variant: product.variant ? product.variant.options_text_analytics : null,
      position: image.dataset.position,
      price,
    })
  }

  /**
   * `on_error` callback for image lazy load on product cards. Replaces the given
   * `image` dom element with a fallback text replacement with the `image`'s '
   * `fallbackText` data attribute value.
   *
   * @param {element} image failed image element that needs to be replaced.
   */
  const replaceImageWithFallback = image => {
    debug.image('FAILURE', image)
    const fallbackText = document.createElement('span')

    if (!image || !('fallbackText' in image.dataset)) {
      return
    }
    fallbackText.className = `product-fallback ${data.style === 'top-ten' ? 'has-drop-shadow' : ''}`
    fallbackText.appendChild(document.createTextNode(image.dataset.fallbackText))
    image.replaceWith(fallbackText)
  }

  /**
   * Adds all variants to variants object
   *
   * @param {array} newProducts - Products to be formatted
   *
   * @returns {array} formatted products
   */
  const setProductVariants = (newProducts = []) =>
    newProducts.map(product => {
      const standardizedProduct = { ...product }

      if (!standardizedProduct.variants && standardizedProduct.variantsIncludingMaster) {
        standardizedProduct.variants = standardizedProduct.variantsIncludingMaster
        delete standardizedProduct.variantsIncludingMaster
      }

      return standardizedProduct
    })

  /**
   * Fetches and formats a list of products from the server.
   * TODO: Someday we might consider abstracting api calls to a separate location.
   */
  const getProductData = () => {
    // This is the order that products should appear in.
    const orderedSkusList = data.products.map(product => product.sku)

    if (data.products && data.products.length && isDynamic) {
      // Make sure products are in camelCase
      const formattedProducts = camelCase(data.products || [])
      const standardizedProducts = setProductVariants(formattedProducts)

      setProducts(standardizedProducts)
    } else {
      // Grab a list of just product skus.
      const path = data.productURL ? data.productURL : `/api/prismic/product_lists/${data.id}`

      debug.data('GET', path)
      debug.performanceStart(path)
      $.getJSON(path)
        .done(response => {
          debug.performanceEnd(path)
          debug.data('SUCCESS', response)

          if (response.status !== 'success') {
            setError(true)
            setLoading(false)

            return
          }

          // Ensure products are in camelCase
          const formattedProducts = camelCase(response.data.products || [])
          const standardizedProducts = setProductVariants(formattedProducts)

          // Sort products to be in requested order
          const sortedProducts = sortProductsByList(orderedSkusList, [...standardizedProducts])

          // Combine prismic data with product data
          // If this list is dynamic, we can assume there is no data to match up.
          // We can also try to set values that might not have been entered in Prismic.
          if (isDynamic) {
            if (response && response.data && response.data.title && !title) {
              setTitle(data.data.title)
            }

            if (response && response.data && response.data.link && !link) {
              setLink(data.data.link)
            }

            if (response && response.data && response.data.linkText && !linkText) {
              setLinkText(data.data.linkText)
            }

            if (response && response.data && response.data.listName && !listName) {
              setListName(data.data.listName)
            }
          }

          setProducts(sortedProducts)
        })
        .fail(response => {
          setError(true)
          setLoading(false)
        })
    }
    setLoading(false)
  }

  /**
   * Renders individual Product components.
   *
   * @returns {function} React component
   */
  const renderProducts = () => {
    if (data.style === 'top-ten') {
      return products.map((product, idx) => (
        <div className="card-wrapper" key={idx}>
          <TopTenProductCard
            product={product}
            loading={loading}
            position={idx + 1}
            listName={listName}
          />
        </div>
      ))
    } else if (data.style === 'vertical') {
      return products.map((product, idx) => (
        <HorizontalProductCard
          product={product}
          loading={loading}
          key={idx}
          position={idx + 1}
          listName={data.uid}
        />
      ))
    }

    return products.map((product, idx) => (
      <div className="card-wrapper" key={idx}>
        <DetailedProductCard
          product={product}
          loading={loading}
          position={idx + 1}
          listName={listName}
        />
      </div>
    ))
  }

  let currentContext = context
  const viewableClass = prismicHelpers.formatViewClasses(data)

  if (currentContext) {
    currentContext = document.querySelector(currentContext)
  }

  if (!prismicHelpers.userHasPermission(data) || (!loading && error)) {
    // Hide currentContext
    if (currentContext) {
      currentContext.classList.add('hidden')
    }

    return false
  }

  // Show currentContext and add visibility classes.
  if (currentContext) {
    currentContext.classList.remove('hidden')
    viewableClass.split(' ').map(className => {
      if (className) currentContext.classList.add(className)
    })
  }

  useEffect(() => {
    // Fetches product data once the component is in the viewport.
    inView(selector).once('enter', getProductData)
  }, [])

  useEffect(() => {
    /**
     * Adds lazy loading of product images.
     */
    new LazyLoad({
      container: document.querySelector(containerSelector),
      elements_selector: `#${uid} .is-lazy`,
      threshold: 100,
      data_src: 'original',
      data_srcset: 'original-set',
      callback_set: trackProductImpression,
      callback_error: replaceImageWithFallback,
    })
  })

  if (error) {
    return null
  }

  return (
    <div
      id={uid}
      className={`product-list ${data.style ||
        'detailed'}-product-list ${viewableClass} ${backgroundClass}`}
    >
      <ThemeOverride
        selector={`#${uid}`}
        textColor={data.primaryColor}
        backgroundColor={data.primaryColor}
        values={{ beforeBackground: helpers.hexToRGBA(data.primaryColor, 0.75) }}
      />
      <Header
        headerType={data.style}
        headerTitle={title}
        headerLink={{
          text: linkText,
          label: data.linkLabel,
          url: link,
        }}
        metaData={{
          name: listName,
          count: products.length,
        }}
      />
      {isHorizontal ? (
        <HorizontalScroller>{renderProducts()}</HorizontalScroller>
      ) : (
        <div className="product-list-content">{renderProducts()}</div>
      )}
    </div>
  )
}

ProductList.defaultProps = {
  data: {
    products: [],
    uid: '',
  },
  context: '',
  isDynamic: false,
}

ProductList.propTypes = {
  data: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.object),
    style: PropTypes.string,
    linkText: PropTypes.string,
    listName: PropTypes.string,
    linkLabel: PropTypes.string,
    primaryColor: PropTypes.string,
    uid: PropTypes.string,
  }),
  context: PropTypes.string,
  isDynamic: PropTypes.bool,
}

export default ProductList
